import React, { useState } from "react";
import NavBar from "../../components/NavBar/NavBar";
import { FaEnvelope, FaPhone, FaMapMarkerAlt, FaClock, FaUser, FaBuilding, FaNewspaper, FaGlobe } from "react-icons/fa";
import "./PressInquiries.css";
import Footer from "../../components/Footer/Footer";
import FloatingWebsiteNavigation from "../../components/FloatingWebsiteNavigation/FloatingWebsiteNavigation";
import { useLanguage } from "../../contexts/LanguageContext";

export default function PressInquiries() {
  const { translations: t } = useLanguage();
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    mediaOutlet: "",
    inquiryType: "",
    subject: "",
    message: "",
    deadline: ""
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  const inquiryTypes = [
    t?.pressInquiries?.inquiryTypes?.interviewRequest || "Interview Request",
    t?.pressInquiries?.inquiryTypes?.productInformation || "Product Information",
    t?.pressInquiries?.inquiryTypes?.companyNews || "Company News",
    t?.pressInquiries?.inquiryTypes?.partnershipInquiry || "Partnership Inquiry",
    t?.pressInquiries?.inquiryTypes?.eventCoverage || "Event Coverage",
    t?.pressInquiries?.inquiryTypes?.backgroundInformation || "Background Information",
    t?.pressInquiries?.inquiryTypes?.other || "Other"
  ];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
      setSubmitStatus("success");
      setFormData({
        name: "",
        email: "",
        company: "",
        mediaOutlet: "",
        inquiryType: "",
        subject: "",
        message: "",
        deadline: ""
      });
    } catch (error) {
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo = [
    {
      icon: FaEnvelope,
      title: t?.pressInquiries?.contactInfo?.pressEmail?.title || "Press Email",
      value: "press@iwinto.com",
      description: t?.pressInquiries?.contactInfo?.pressEmail?.description || "For all media inquiries and press releases"
    },
    {
      icon: FaPhone,
      title: t?.pressInquiries?.contactInfo?.pressHotline?.title || "Press Hotline",
      value: "+971 50 378 0153",
      description: t?.pressInquiries?.contactInfo?.pressHotline?.description || "Available 9 AM - 6 PM EST, Monday - Sunday"
    },
    {
      icon: FaMapMarkerAlt,
      title: t?.pressInquiries?.contactInfo?.headquarters?.title || "Headquarters",
      value: "Dubai, UAE",
      description: t?.pressInquiries?.contactInfo?.headquarters?.description || "Main office location for in-person meetings"
    },
    {
      icon: FaClock,
      title: t?.pressInquiries?.contactInfo?.responseTime?.title || "Response Time",
      value: "12-24 hours",
      description: t?.pressInquiries?.contactInfo?.responseTime?.description || "Typical response time for press inquiries"
    }
  ];

  const pressTeam = [
    {
      name: "Omar Al Mansoori",
      title: t?.pressInquiries?.pressTeam?.omar?.title || "Head of Communications",
      email: "Omar.AlMansoori@iwinto.com",
      expertise: t?.pressInquiries?.pressTeam?.omar?.expertise || "Media Relations, Crisis Communications"
    },
    {
      name: "Ali Al Fardan",
      title: t?.pressInquiries?.pressTeam?.ali?.title || "Press Relations Manager",
      email: "Al.AlFardan@iwinto.com",
      expertise: t?.pressInquiries?.pressTeam?.ali?.expertise || "Product Launches, Tech Media"
    },
    {
      name: "Fatima Zahra",
      title: t?.pressInquiries?.pressTeam?.fatima?.title || "Content & PR Specialist",
      email: "Fatima.Zahra@iwinto.com",
      expertise: t?.pressInquiries?.pressTeam?.fatima?.expertise || "Content Strategy, Social Media"
    }
  ];

  return (
    <>
      <div className="nav-desktop-only">
        <NavBar />
      </div>
      <div className="press-inquiries-page">

      <header className="press-inquiries-hero">
        <div className="press-inquiries-hero-inner">
          <h1 className="press-inquiries-title">{t?.pressInquiries?.title || "Press Inquiries"}</h1>
          <p className="press-inquiries-subtitle">
            {t?.pressInquiries?.subtitle || "Get in touch with our press team for media inquiries, interviews, and company information."}
          </p>
        </div>
      </header>

      {/* Contact Information Cards */}
      <section className="contact-info-section">
        <div className="contact-info-grid">
          {contactInfo.map((info, index) => {
            const IconComponent = info.icon;
            return (
              <div className="contact-info-card" key={index}>
                <div className="contact-info-icon">
                  <IconComponent />
                </div>
                <div className="contact-info-content">
                  <h3 className="contact-info-title">{info.title}</h3>
                  <p className="contact-info-value">{info.value}</p>
                  <p className="contact-info-description">{info.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Press Team */}
      <section className="press-team-section">
        <div className="press-team-header">
          <h2 className="press-team-title">{t?.pressInquiries?.pressTeam?.title || "Our Press Team"}</h2>
          <p className="press-team-subtitle">
            {t?.pressInquiries?.pressTeam?.subtitle || "Meet the experts who can help with your media needs"}
          </p>
        </div>
        <div className="press-team-grid">
          {pressTeam.map((member, index) => (
            <div className="press-team-card" key={index}>
              <div className="press-team-avatar">
                <FaUser />
              </div>
              <div className="press-team-info">
                <h3 className="press-team-name">{member.name}</h3>
                <p className="press-team-title">{member.title}</p>
                <p className="press-team-email">{member.email}</p>
                <p className="press-team-expertise">{member.expertise}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Inquiry Form */}
      <section className="inquiry-form-section">
        <div className="inquiry-form-header">
          <h2 className="inquiry-form-title">{t?.pressInquiries?.form?.title || "Send Us an Inquiry"}</h2>
          <p className="inquiry-form-subtitle">
            {t?.pressInquiries?.form?.subtitle || "Fill out the form below and we'll get back to you within 12-24 hours"}
          </p>
        </div>

        <form className="inquiry-form" onSubmit={handleSubmit}>
          <div className="form-row">
            <div className="form-group">
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                required
                className="form-input"
                placeholder=""
              />
              <label htmlFor="name" className="form-label">
                <FaUser className="form-label-icon" />
                {t?.pressInquiries?.form?.labels?.fullName || "Full Name"} <span className="required-asterisk">*</span>
              </label>
            </div>
            <div className="form-group">
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                required
                className="form-input"
                placeholder=""
              />
              <label htmlFor="email" className="form-label">
                <FaEnvelope className="form-label-icon" />
                {t?.pressInquiries?.form?.labels?.emailAddress || "Email Address"} <span className="required-asterisk">*</span>
              </label>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <input
                type="text"
                id="company"
                name="company"
                value={formData.company}
                onChange={handleInputChange}
                className="form-input"
                placeholder=""
              />
              <label htmlFor="company" className="form-label">
                <FaBuilding className="form-label-icon" />
                {t?.pressInquiries?.form?.labels?.company || "Company/Organization"}
              </label>
            </div>
            <div className="form-group">
              <input
                type="text"
                id="mediaOutlet"
                name="mediaOutlet"
                value={formData.mediaOutlet}
                onChange={handleInputChange}
                className="form-input"
                placeholder=""
              />
              <label htmlFor="mediaOutlet" className="form-label">
                <FaNewspaper className="form-label-icon" />
                {t?.pressInquiries?.form?.labels?.mediaOutlet || "Media Outlet"}
              </label>
            </div>
          </div>

          <div className="form-row">
            <div className="form-group">
              <select
                id="inquiryType"
                name="inquiryType"
                value={formData.inquiryType}
                onChange={handleInputChange}
                required
                className="form-select"
              >
                <option value="">{t?.pressInquiries?.form?.labels?.selectInquiryType || "Select inquiry type"}</option>
                {inquiryTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              <label htmlFor="inquiryType" className="form-label">
                <FaGlobe className="form-label-icon" />
                {t?.pressInquiries?.form?.labels?.inquiryType || "Type of Inquiry"} <span className="required-asterisk">*</span>
              </label>
            </div>
            <div className="form-group">
              <input
                type="date"
                id="deadline"
                name="deadline"
                value={formData.deadline}
                onChange={handleInputChange}
                className="form-input"
                placeholder=""
              />
              <label htmlFor="deadline" className="form-label">
                <FaClock className="form-label-icon" />
                {t?.pressInquiries?.form?.labels?.deadline || "Deadline (if applicable)"}
              </label>
            </div>
          </div>

          <div className="form-group">
            <input
              type="text"
              id="subject"
              name="subject"
              value={formData.subject}
              onChange={handleInputChange}
              required
              className="form-input"
              placeholder=""
            />
            <label htmlFor="subject" className="form-label">
              {t?.pressInquiries?.form?.labels?.subject || "Subject"} <span className="required-asterisk">*</span>
            </label>
          </div>

          <div className="form-group textarea-group">
            <textarea
              id="message"
              name="message"
              value={formData.message}
              onChange={handleInputChange}
              required
              className="form-textarea"
              rows="6"
              placeholder=""
            />
            <label htmlFor="message" className="form-label">
              {t?.pressInquiries?.form?.labels?.message || "Message"} <span className="required-asterisk">*</span>
            </label>
          </div>

          {submitStatus === "success" && (
            <div className="form-success">
              <FaEnvelope />
              <span>{t?.pressInquiries?.form?.messages?.success || "Thank you! Your inquiry has been submitted successfully. We'll get back to you within 24-48 hours."}</span>
            </div>
          )}

          {submitStatus === "error" && (
            <div className="form-error">
              <span>{t?.pressInquiries?.form?.messages?.error || "Sorry, there was an error submitting your inquiry. Please try again or contact us directly."}</span>
            </div>
          )}

          <button
            type="submit"
            className="form-submit-btn"
            disabled={isSubmitting}
          >
            {isSubmitting ? (t?.pressInquiries?.form?.submitting || "SUBMITTING...") : (t?.pressInquiries?.form?.submit || "SUBMIT INQUIRY")}
          </button>
        </form>
      </section>

      <Footer />
      </div>
      <FloatingWebsiteNavigation />
    </>
  );
}
